import { Grid, UI, Utils } from 'common';
import isObject from 'lodash/isObject';
import React, { Component, Fragment } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import FundAmountCellRenderer from './FundAmountCellRenderer.component';
import ReviewTransactionModal from './ReviewTransactionModal.component';
import TransactionConfirmationModal from './TransactionConfirmationModal.component';

const { WrappedButton, WrappedCheckbox, WrappedDropdownWithLabel, Spinner } =
	UI;
const { Logger, Session, UserRole, Util, withLayoutAndModalContexts } = Utils;
const { GridPanel } = Grid;

class AllocateFunds extends Component {
	constructor() {
		super();

		this.state = {
			billingContactInfo: {},
			columnDefs: [
				{
					headerName: 'Locations',
					field: 'location',
					filter: true,
					flex: 1,
					sortable: true,
				},
				{
					headerName: 'Fund Amount',
					cellRenderer: 'FundAmountCellRenderer',
					cellRendererParams: {
						validate: this.validate,
					},
					field: 'fundAmount',
					filter: false,
					maxWidth: 200,
					sortable: false,
				},
			],
			isLoading: false, // true to show a loading spinner
			loadingMessage: '', // message to display in the spinner
			paymentMethod: 0,
			paymentMethods: [
				{
					name: 'ACH Credit',
					value: 2,
				},
				{
					name: 'Check',
					value: 1,
				},
				{
					name: 'Overdraft',
					value: 3,
				},
			],
			rowData: [],
			termsChecked: false,
			validated: false,
		};
	}

	componentDidMount = async () => {
		const userRole = Session.getUserRole();

		this.displayLoadingSpinner(true, 'Loading locations...');

		await this.getLocations();

		// Per ATG, only the following roles are supported.
		// TODO: currently there's an issue with RegionAdmin user. See WEBAPPS-24322.
		if (userRole === UserRole.CorpAdmin || userRole === UserRole.RegionAdmin) {
			await this.getBillingContactInfo();
		}

		this.displayLoadingSpinner(false);
	};

	getBillingContactInfo = async () => {
		const {
			modalContext: { showError },
		} = this.props;

		const response = await AccountCommunication.getCorporationContactInfo();

		if (!AccountCommunication.isSuccessful(response)) {
			return showError({ response });
		}

		const contactInfo = isObject(response.data) ? response.data : {};
		const contactInfoAddress =
			isObject(contactInfo.BillingContact) &&
			isObject(contactInfo.BillingContact.Address)
				? {
						...contactInfo.BillingContact.Address,
						Zip: Util.formatZip(contactInfo.BillingContact.Address.Zip),
					}
				: {};
		const billingContactInfo = isObject(contactInfo.BillingContact)
			? {
					...contactInfo.BillingContact,
					Address: contactInfoAddress,
					PhoneNumber: Util.formatPhoneNumber(
						contactInfo.BillingContact.PhoneNumber
					),
				}
			: {};

		this.setState({
			billingContactInfo,
		});
	};

	getLocations = async () => {
		const {
			modalContext: { showError },
		} = this.props;

		const response = await AccountCommunication.getLocations();

		if (AccountCommunication.isSuccessful(response)) {
			const responseData = response.data || [];

			this.setState({
				rowData: responseData.reduce(
					(_rowData, { CustId, MeterAccount = {}, AccountStatus }) => {
						// Filter by AccountStatus 'active' (WEBAPPS-29270)
						if (AccountStatus === 'Active') {
							_rowData.push({
								custId: CustId,
								fundAmount: 0,
								location: `${MeterAccount.Name} (${MeterAccount.MeterNo})`,
							});
						}
						return _rowData;
					},
					[]
				),
			});
		} else {
			showError({
				response,
			});
		}
	};

	displayLoadingSpinner = (isLoading, loadingMessage = '') => {
		this.setState({
			isLoading,
			loadingMessage,
		});
	};

	validate = () => {
		this.setState(({ paymentMethod, termsChecked, rowData }) => ({
			validated:
				// payment method should be selected
				paymentMethod !== 0 &&
				// terms and condition should be checked
				termsChecked &&
				// allocated funds are within $10000
				rowData.every(({ fundAmount }) => fundAmount <= 10000),
		}));
	};

	setAllocatedFund = async reviewTransactionModalId => {
		const {
			modalContext: { closeModal, showError, showModal },
		} = this.props;
		const { paymentMethod, rowData } = this.state;
		const request = {
			AllocatedFundType: paymentMethod,
			AllocatedFundItems: rowData.map(
				({ custId: CustId, fundAmount: Amount }) => ({
					CustId,
					Amount,
				})
			),
		};

		Logger.saveActionData('Set Allocated Funds Button Clicked');

		closeModal(reviewTransactionModalId);

		this.displayLoadingSpinner(true, 'Submitting request...');

		const response = await AccountCommunication.setAllocatedFund(request);

		this.displayLoadingSpinner(false);

		if (!AccountCommunication.isSuccessful(response)) {
			showError({
				response,
			});

			return;
		}

		showModal({
			component: TransactionConfirmationModal,
			data: {
				responseData: response.data,
				locationRows: rowData,
			},
		});
	};

	onPaymentMethodChange = e => {
		if (e.value) {
			this.setState(
				{
					paymentMethod: e.value,
				},
				this.validate
			);
		}
	};

	onClickCheckbox = e => {
		this.setState(
			{
				termsChecked: e.checked,
			},
			this.validate
		);
	};

	onContinueButtonClick = () => {
		const {
			modalContext: { showError, showModal },
		} = this.props;
		const { paymentMethod, rowData } = this.state;
		let totalAmount = 0,
			locationsAllocated = 0;

		rowData.forEach(location => {
			if (location.fundAmount > 0) {
				totalAmount += location.fundAmount;
				locationsAllocated += 1;
			}
		});

		if (totalAmount === 0) {
			showError({
				data: {
					title: 'Incomplete Fields',
					description: `
						<p>
							The following fields are incomplete or in error:
							<br /><br />
							<strong>Fund Amount:</strong> Total fund Amount must exceed $0.
						</p>
					`,
					primaryButtonText: 'Close',
					handleType: 'custom',
				},
			});

			return;
		}

		showModal({
			component: ReviewTransactionModal,
			data: {
				totalAmount,
				locationsAllocated,
				paymentMethod,
				billingAddress: this.renderBillingAddress(),
				callback: this.setAllocatedFund,
			},
		});
	};

	onGridReady = ({ api }) => {
		this.gridApi = api;
	};

	// update column state when user sorts the column
	onSortChanged = sortModel => {
		this.gridApi.setSortModel(sortModel);
	};

	renderBillingAddress = () => {
		const { billingContactInfo } = this.state;
		const billingAddress = isObject(billingContactInfo.Address)
			? billingContactInfo.Address
			: {};

		// build fullname
		const fullName = [
			billingContactInfo.FirstName,
			billingContactInfo.MiddleName,
			billingContactInfo.LastName,
		]
			.filter(name => name)
			.join(' ');

		// build city state, zip
		const cityStateAndZip = [
			billingAddress.City ? `${billingAddress.City},` : null,
			billingAddress.State,
			billingAddress.Zip,
		]
			.filter(name => name)
			.join(' ');

		return (
			<p>
				{[
					fullName,
					billingContactInfo.CompanyName,
					billingAddress.Address1,
					billingAddress.Address2,
					cityStateAndZip,
					billingContactInfo.PhoneNumber,
					billingContactInfo.Email,
				]
					.filter(item => item) // filter out null items
					.map((str, i) => (
						<Fragment key={i}>
							{str}
							<br />
						</Fragment>
					))}
			</p>
		);
	};

	render() {
		const {
			columnDefs,
			isLoading,
			paymentMethod,
			paymentMethods,
			rowData,
			termsChecked,
			validated,
			loadingMessage,
		} = this.state;
		const {
			layoutContext: { isMobile },
		} = this.props;
		const mobileClassName = isMobile ? 'mobile-wrapper' : '';

		return (
			<div className="account-allocate-funds">
				{/* This wrapper is to prevent modals' styling get affected */}
				<div className="allocate-funds-wrapper">
					<div className="body">
						<div className="account-title">Allocate Funds</div>
						<p className={`header-text ${mobileClassName}`}>
							Fund locations using ACH Credit, Check, or Overdraft:
						</p>
						<WrappedDropdownWithLabel
							className={`dropdown ${mobileClassName}`}
							label="Payment Method:"
							uiElementConfig={{
								name: 'paymentMethod',
								value: paymentMethod,
								placeholder: 'Select Payment Method',
								onChange: this.onPaymentMethodChange,
								options: paymentMethods,
								optionLabel: 'name',
								dataqa: 'paymentmethod',
							}}
						/>
						<div
							className={`common-ui-element-wrapper billing-address ${mobileClassName}`}
						>
							<div className="label-container">
								<p>Billing Address:</p>
							</div>
							<div className="p-grid p-nogutter p-dir-col">
								{this.renderBillingAddress()}
								<p>
									<a href="./contactinfo">Edit</a>
								</p>
							</div>
						</div>
						<div className="grid-container">
							<GridPanel
								rowData={rowData}
								columnDefs={columnDefs}
								frameworkComponents={{ FundAmountCellRenderer }}
								showPagination={false}
								dataqa="allocatefundslocations-grid"
								onGridReady={this.onGridReady}
								onSortChanged={this.onSortChanged}
							/>
						</div>
					</div>
					<div className="footer">
						<WrappedCheckbox
							name="termsCheckbox"
							checked={termsChecked}
							onChange={this.onClickCheckbox}
							dataqa="termscheckbox"
						>
							<p>
								I agree to pay the Stamps.com fees associated with this payment
								method as set forth in the{' '}
								<a
									href={`https://account.web.stamps.com/terms/General`}
									target="_blank"
									rel="noreferrer noopener"
								>
									terms and conditions
								</a>
								, if applicable.
							</p>
						</WrappedCheckbox>
						<WrappedButton
							className={`save-button ${mobileClassName}`}
							label="Continue"
							disabled={!validated}
							onClick={this.onContinueButtonClick}
							dataqa="allocate-funds-continue"
						/>
					</div>
				</div>
				{/* per WEBAPPS-26026, display a loading mask over the page when a user presses Submit
					per WEBAPPS-28068, the Spinner message will be dinamically handled for first time load
					and submitting purposes */}
				{isLoading && <Spinner msg={loadingMessage} />}
			</div>
		);
	}
}

export default withLayoutAndModalContexts(AllocateFunds);
